import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixQuickpagesPublishedpagesV1PublishedPagesUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/quick-pages/v1/published-pages',
        destPath: '/v1/published-pages',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/quick-pages/v1/published-pages',
        destPath: '/v1/published-pages',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_quick-pages_published-pages';

/** Retrieves a PublishedPage. */
export function getPublishedPage(payload: object): RequestOptionsFactory<any> {
  function __getPublishedPage({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.published_page',
      method: 'GET' as any,
      methodFqn:
        'wix.quickpages.publishedpages.v1.PublishedPages.GetPublishedPage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPublishedpagesV1PublishedPagesUrl({
        protoPath: '/v1/published-pages/{publishedPageId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'publishedPage.createdDate' },
              { path: 'publishedPage.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPublishedPage;
}

/** Retrieves a PublishedPage by slug. */
export function getPublishedPageBySlug(
  payload: object
): RequestOptionsFactory<any> {
  function __getPublishedPageBySlug({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.published_page',
      method: 'GET' as any,
      methodFqn:
        'wix.quickpages.publishedpages.v1.PublishedPages.GetPublishedPageBySlug',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPublishedpagesV1PublishedPagesUrl({
        protoPath: '/v1/published-pages/{slug}/slug',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'publishedPage.createdDate' },
              { path: 'publishedPage.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPublishedPageBySlug;
}

/** Retrieves a PublishedPage by slug. */
export function getPublishedPageByPageId(
  payload: object
): RequestOptionsFactory<any> {
  function __getPublishedPageByPageId({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.published_page',
      method: 'GET' as any,
      methodFqn:
        'wix.quickpages.publishedpages.v1.PublishedPages.GetPublishedPageByPageId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPublishedpagesV1PublishedPagesUrl({
        protoPath: '/v1/published-pages/{pageId}/page-id',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'publishedPage.createdDate' },
              { path: 'publishedPage.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPublishedPageByPageId;
}

/**
 * Retrieves a list of PublishedPages, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 PublishedPages can be returned per request.
 *
 * To learn how to query PublishedPages, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 */
export function queryPublishedPages(
  payload: object
): RequestOptionsFactory<any> {
  function __queryPublishedPages({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.published_page',
      method: 'GET' as any,
      methodFqn:
        'wix.quickpages.publishedpages.v1.PublishedPages.QueryPublishedPages',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPublishedpagesV1PublishedPagesUrl({
        protoPath: '/v1/published-pages/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'publishedPages.createdDate' },
              { path: 'publishedPages.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixQuickpagesPublishedpagesV1PublishedPagesUrl({
            protoPath: '/v1/published-pages/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryPublishedPages;
}
